/**
 * Delivery Modal Step Navigation
 * Handles the flow between steps: zip -> preferences -> summary
 */

(function initializeStepNavigation() {
  // Step constants
  const STEPS = {
    ZIP: 'zip',
    PREFERENCES: 'preferences',
    SUMMARY: 'summary'
  };

  const STEP_ORDER = [STEPS.ZIP, STEPS.PREFERENCES, STEPS.SUMMARY];

  // State
  let currentStep = STEPS.ZIP;
  let currentSubstepIndex = 0;
  let substeps = [];
  let isEditingFromSummary = false;

  /**
   * Get all step elements
   */
  function getStepElement(stepName) {
    return document.querySelector(`[data-delivery-modal-step-selector="${stepName}"]`);
  }

  /**
   * Get the footer element
   */
  function getFooterElement() {
    return document.querySelector('[data-delivery-modal-step-footer]');
  }

  /**
   * Check if pickup is selected
   */
  function isPickupSelected() {
    const pickupTab = document.getElementById('pickup-tab');
    return pickupTab ? pickupTab.classList.contains('active') : false;
  }

  /**
   * Get the selected delivery option (delivery or pickup)
   */
  function getSelectedDeliveryOption() {
    const deliveryTab = document.getElementById('delivery-tab');
    const pickupTab = document.getElementById('pickup-tab');
    
    if (deliveryTab && deliveryTab.classList.contains('active')) {
      return 'delivery';
    }
    if (pickupTab && pickupTab.classList.contains('active')) {
      return 'pickup';
    }
    return '';
  }

  /**
   * Populate form fields with saved values when coming from saved state
   */
  function populateFormFieldsFromSavedState() {
    if (hasSavedSelections()) {
      const savedTime = window.EtStorage.local.getItem('selected_fulfillment_time');
      if (savedTime) {
        const timeSelect = document.getElementById('fulfillment-time-delivery');
        if (timeSelect) {
          timeSelect.value = savedTime;
        }
      }
    }
  }

  /**
   * Gather all user selections for the summary
   */
  function gatherUserSelections() {
    // If editing from summary, use form fields; if on initial summary from saved state, use localStorage
    let currentZipInput = '';
    let currentDeliveryType = '';
    let currentDeliveryTime = '';
    
    if (isEditingFromSummary) {
      // User clicked edit and may have changed values - use form fields
      currentZipInput = document.getElementById('zip')?.value?.trim() || '';
      currentDeliveryType = getSelectedDeliveryOption() || '';
      currentDeliveryTime = document.getElementById('fulfillment-time-delivery')?.value || '';
    } else if (hasSavedSelections()) {
      // Initial summary load with saved data - use localStorage only
      currentZipInput = window.EtStorage.local.getItem('selected_zip_code') || '';
      currentDeliveryType = window.EtStorage.local.getItem('selected_delivery_type') || '';
      currentDeliveryTime = window.EtStorage.local.getItem('selected_fulfillment_time') || '';
    } else {
      // Fresh flow from step 1 - use form fields
      currentZipInput = document.getElementById('zip')?.value?.trim() || '';
      currentDeliveryType = getSelectedDeliveryOption() || '';
      currentDeliveryTime = document.getElementById('fulfillment-time-delivery')?.value || '';
    }
    
    const selectedPickupRadio = document.querySelector('input[name="pickup-location"]:checked');
    const currentPickupLocationId = selectedPickupRadio ? selectedPickupRadio.getAttribute('data-location-id') || '' : '';
    
    const fulfillmentConstants = window.FulfillmentConstants || {};
    const snapshot = (window.FulfillmentState && window.FulfillmentState._snapshot) || {};
    const committedZip = snapshot[fulfillmentConstants.ZIP_CODE || 'selected_zip_code'] || '';
    const committedLocationId = snapshot[fulfillmentConstants.LOCATION_ID || 'selected_location_id'] || '';
    const committedDeliveryType = snapshot[fulfillmentConstants.DELIVERY_TYPE || 'selected_delivery_type'] || '';

    return {
      currentZipInput,
      selectedPickupRadio,
      currentPickupLocationId,
      currentDeliveryType,
      currentDeliveryTime,
      fulfillmentConstants,
      snapshot,
      committedZip,
      committedLocationId,
      committedDeliveryType
    };
  }

  /**
   * Format a date string (YYYY-MM-DD) into readable format
   * Parses without timezone conversion to avoid off-by-one errors
   */
  function formatDateString(dateString) {
    try {
      // Parse date string as YYYY-MM-DD without timezone conversion
      const parts = dateString.split('-');
      const year = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1; // months are 0-indexed
      const day = parseInt(parts[2], 10);
      
      // Create date using local timezone, not UTC
      const dateObj = new Date(year, month, day);
      
      const dayOfWeekOptions = { weekday: 'long' };
      const dayOfWeekFull = dateObj.toLocaleDateString('en-US', dayOfWeekOptions);
      const monthName = dateObj.toLocaleDateString('en-US', { month: 'long' });
      const dayNumber = dateObj.getDate();
      let ordinalSuffix = 'th';
      if (dayNumber % 10 === 1 && dayNumber !== 11) ordinalSuffix = 'st';
      else if (dayNumber % 10 === 2 && dayNumber !== 12) ordinalSuffix = 'nd';
      else if (dayNumber % 10 === 3 && dayNumber !== 13) ordinalSuffix = 'rd';
      const yearNumber = dateObj.getFullYear();
      return dayOfWeekFull + ', ' + monthName + ' ' + dayNumber + ordinalSuffix + ', ' + yearNumber;
    } catch (e) {
      return '';
    }
  }

  /**
   * Populate summary info elements with user selections
   */
  function populateSummaryInfo(selections) {
    // Populate delivery method
    const methodElements = document.querySelectorAll('[data-summary-info="method"]');
    let methodText = '';
    if (selections.currentDeliveryType) {
      const typeDisplay = selections.currentDeliveryType === 'pickup' ? 'Pick-up' : 'Delivery';
      methodText = typeDisplay + ' - ' + selections.currentZipInput;
    }
    methodElements.forEach(element => {
      element.textContent = methodText;
    });

    // Populate location
    const locationElements = document.querySelectorAll('[data-summary-info="location"]');
    let locationText = '';
    if (selections.selectedPickupRadio) {
      const locationName = selections.selectedPickupRadio.dataset.locationName || '';
      let address1 = '';
      try {
        const locationData = JSON.parse(selections.selectedPickupRadio.dataset.location);
        address1 = locationData.shopifyLocation.address.address1 || '';
      } catch (e) {
        // Handle parsing error silently
      }
      locationText = locationName + (address1 ? ' - ' + address1 : '');
    }
    locationElements.forEach(element => {
      element.textContent = locationText;
    });

    // Helper function to format date from calendar
    function formatCalendarDate(calendarSelector, monthSelector) {
      let dateText = '';
      const selectedDateElement = document.querySelector(calendarSelector + ' .et-affinity-reschedule-calendar__day--selected');
      const monthDisplay = document.querySelector(monthSelector)?.textContent || '';
      
      if (selectedDateElement && monthDisplay) {
        try {
          const dayNumber = parseInt(selectedDateElement.textContent, 10);
          // Parse month display (e.g., "June 2026" or "June")
          const monthParts = monthDisplay.trim().split(/\s+/);
          const monthName = monthParts[0];
          let year = new Date().getFullYear();
          if (monthParts.length > 1) {
            year = parseInt(monthParts[1], 10);
          }
          
          // Create date object to get day of week
          const dateObj = new Date(year, new Date(`${monthName} 1, ${year}`).getMonth(), dayNumber);
          
          // Format day of week (full name)
          const dayOfWeekOptions = { weekday: 'long' };
          const dayOfWeekFull = dateObj.toLocaleDateString('en-US', dayOfWeekOptions);
          
          // Add ordinal suffix to day (1st, 2nd, 3rd, 4th, etc.)
          let ordinalSuffix = 'th';
          if (dayNumber % 10 === 1 && dayNumber !== 11) ordinalSuffix = 'st';
          else if (dayNumber % 10 === 2 && dayNumber !== 12) ordinalSuffix = 'nd';
          else if (dayNumber % 10 === 3 && dayNumber !== 13) ordinalSuffix = 'rd';
          
          // Format as "Tuesday, October 20th, 2026"
          dateText = dayOfWeekFull + ', ' + monthName + ' ' + dayNumber + ordinalSuffix + ', ' + year;
        } catch (e) {
          // Handle parsing error silently
        }
      }
      return dateText;
    }

    // Populate date from pickup calendar or localStorage
    const dateElements = document.querySelectorAll('[data-summary-info="date"]');
    let pickupDateText = '';
    
    // Determine where to get the date from
    if (isEditingFromSummary) {
      // User edited - use calendar
      pickupDateText = formatCalendarDate('#pickup-calendar', '#pickup-calendar-month');
    } else if (hasSavedSelections()) {
      // Initial load with saved data - use localStorage
      const savedDate = window.EtStorage.local.getItem('selected_fulfillment_date');
      if (savedDate) {
        pickupDateText = formatDateString(savedDate);
      }
    } else {
      // Fresh flow - use calendar
      pickupDateText = formatCalendarDate('#pickup-calendar', '#pickup-calendar-month');
    }
    
    dateElements.forEach(element => {
      element.textContent = pickupDateText;
    });

    // Populate date and time from delivery calendar or localStorage
    const dateTimeElements = document.querySelectorAll('[data-summary-info="date-time"]');
    let deliveryDateText = '';
    let deliveryTime = selections.currentDeliveryTime || '';
    
    // Determine where to get the date from
    if (isEditingFromSummary) {
      // User edited - use calendar
      deliveryDateText = formatCalendarDate('#delivery-calendar', '#delivery-calendar-month');
    } else if (hasSavedSelections()) {
      // Initial load with saved data - use localStorage
      const savedDate = window.EtStorage.local.getItem('selected_fulfillment_date');
      if (savedDate) {
        deliveryDateText = formatDateString(savedDate);
      }
    } else {
      // Fresh flow - use calendar
      deliveryDateText = formatCalendarDate('#delivery-calendar', '#delivery-calendar-month');
    }
    
    dateTimeElements.forEach(element => {
      element.innerHTML = '';
      const dateSpan = document.createElement('span');
      dateSpan.textContent = deliveryDateText;
      element.appendChild(dateSpan);
      
      if (deliveryTime) {
        const timeSpan = document.createElement('span');
        timeSpan.textContent = deliveryTime;
        element.appendChild(timeSpan);
      }
    });
  }

  /**
   * Update summary section visibility based on delivery/pickup selection
   */
  function updateSummarySections() {
    const isPickup = isPickupSelected();
    
    // Show/hide summary sections
    document.querySelectorAll('[data-summary]').forEach(element => {
      const summaryType = element.getAttribute('data-summary');
      if ((isPickup && summaryType === 'pickup') || (!isPickup && summaryType === 'delivery')) {
        element.classList.remove('hidden');
      } else {
        element.classList.add('hidden');
      }
    });
  }

  /**
   * Get all substeps for the current section
   */
  function loadSubsteps() {
    const pickupSection = document.getElementById('pickup-section');
    if (pickupSection && !pickupSection.classList.contains('hidden')) {
      substeps = Array.from(pickupSection.querySelectorAll('[data-delivery-modal-substep]'));
      return substeps;
    }
    substeps = [];
    return substeps;
  }

  /**
   * Show a specific substep and hide others
   */
  function showSubstep(index) {
    substeps.forEach((substep, i) => {
      if (i === index) {
        substep.classList.remove('hidden');
        substep.classList.add('reveal');
      } else {
        substep.classList.add('hidden');
        substep.classList.remove('reveal');
      }
    });
    currentSubstepIndex = index;
  }

  /**
   * Check if we're currently viewing substeps
   */
  function isOnSubstep() {
    return currentStep === STEPS.PREFERENCES && isPickupSelected() && substeps.length > 0;
  }

  /**
   * Get navigation buttons
   */
  function getBackButton() {
    return document.querySelector('[data-delivery-modal-nav-button="back"]');
  }

  function getCancelButton() {
    return document.querySelector('[data-delivery-modal-nav-button="cancel"]');
  }

  function getContinueButton() {
    return document.querySelector('[data-delivery-modal-nav-button="continue"]');
  }

  /**
   * Show a specific step and hide all others
   */
  function showStep(stepName) {
    STEP_ORDER.forEach(step => {
      const element = getStepElement(step);
      if (element) {
        if (step === stepName) {
          element.classList.remove('hidden');
          element.classList.add('reveal');
        } else {
          element.classList.add('hidden');
          element.classList.remove('reveal');
        }
      }
    });

    currentStep = stepName;
    currentSubstepIndex = 0;

    // If entering preferences step, populate form fields from saved state if applicable
    if (stepName === STEPS.PREFERENCES) {
      populateFormFieldsFromSavedState();
      
      if (isPickupSelected()) {
        loadSubsteps();
        if (substeps.length > 0) {
          showSubstep(0);
        }
      } else {
        substeps = [];
      }
    } else {
      substeps = [];
    }

    // If entering summary step, update summary sections and gather user selections
    if (stepName === STEPS.SUMMARY) {
      const selections = gatherUserSelections();
      updateSummarySections();
      populateSummaryInfo(selections);
      // Expose selections for use by other scripts or debugging
      window.deliveryStepNavigation.currentSelections = selections;
      // Reset edit flag since we're back to summary
      isEditingFromSummary = false;
    }

    updateFooterButtons();
  }

  /**
   * Update the state of back/continue buttons based on current step
   */
  function updateFooterButtons() {
    const backBtn = getBackButton();
    const cancelBtn = getCancelButton();
    const continueBtn = getContinueButton();
    const browseMenuBtn = document.getElementById('browse-menu-button');

    if (!backBtn || !continueBtn) return;

    // On summary step, hide all navigation buttons and show browse-menu button
    if (currentStep === STEPS.SUMMARY) {
      if (backBtn) backBtn.classList.add('hidden');
      if (cancelBtn) cancelBtn.classList.add('hidden');
      continueBtn.classList.add('hidden');
      if (browseMenuBtn) {
        browseMenuBtn.classList.remove('hidden');
      }
    } else if (currentStep === STEPS.ZIP && !isOnSubstep()) {
      // On step 1, show cancel button instead of back
      backBtn.classList.add('hidden');
      if (cancelBtn) cancelBtn.classList.remove('hidden');
      continueBtn.classList.remove('hidden');
      if (browseMenuBtn) {
        browseMenuBtn.classList.add('hidden');
      }
      continueBtn.textContent = 'Get Started';
    } else {
      // On other steps, show back button and hide cancel
      backBtn.classList.remove('hidden');
      if (cancelBtn) cancelBtn.classList.add('hidden');
      continueBtn.classList.remove('hidden');
      if (browseMenuBtn) {
        browseMenuBtn.classList.add('hidden');
      }

      // Update Back button state
      backBtn.disabled = false;
      backBtn.textContent = 'Back';

      // Update Continue button
      continueBtn.textContent = 'Continue';
    }
  }

  /**
   * Proceed to the next step (helper function)
   */
  function proceedToNextStep() {
    // If on substeps, advance to next substep
    if (isOnSubstep()) {
      if (currentSubstepIndex < substeps.length - 1) {
        // More substeps to show
        showSubstep(currentSubstepIndex + 1);
        updateFooterButtons();
      } else {
        // Finished all substeps, move to summary
        const currentIndex = STEP_ORDER.indexOf(currentStep);
        if (currentIndex < STEP_ORDER.length - 1) {
          const nextStep = STEP_ORDER[currentIndex + 1];
          showStep(nextStep);
        }
      }
    } else {
      // Regular step navigation
      const currentIndex = STEP_ORDER.indexOf(currentStep);
      if (currentIndex < STEP_ORDER.length - 1) {
        const nextStep = STEP_ORDER[currentIndex + 1];
        showStep(nextStep);
      }
    }
  }

  /**
   * Navigate to next step (or next substep if applicable)
   */
  function goToNextStep() {
    // On step 1, validate zip code before proceeding
    if (currentStep === STEPS.ZIP) {
      const zipInput = document.getElementById('zip');
      
      if (!zipInput) return;
      
      // If zip input is focused, blur it to trigger delivery-modal.js validation
      if (document.activeElement === zipInput) {
        zipInput.blur();
        
        // Wait for validation to complete, then check if there are errors
        setTimeout(() => {
          if (!zipInput.classList.contains('error')) {
            proceedToNextStep();
          }
        }, 200);
        return;
      }
      
      // Input is not focused, check if there's an error class
      if (zipInput.classList.contains('error')) {
        return;
      }
      
      proceedToNextStep();
      return;
    }

    proceedToNextStep();
  }

  /**
   * Navigate to previous step (or previous substep if applicable)
   */
  function goToPreviousStep() {
    // If on substeps and not on first substep, go back to previous substep
    if (isOnSubstep() && currentSubstepIndex > 0) {
      showSubstep(currentSubstepIndex - 1);
      updateFooterButtons();
    } else {
      // Regular step navigation (or back from first substep)
      const currentIndex = STEP_ORDER.indexOf(currentStep);
      if (currentIndex > 0) {
        const previousStep = STEP_ORDER[currentIndex - 1];
        showStep(previousStep);
      }
    }
  }

  /**
   * Navigate to a specific step/substep
   */
  function goToStepAndSubstep(stepName, substepName) {
    // Mark that user is editing from summary
    if (stepName !== STEPS.SUMMARY) {
      isEditingFromSummary = true;
    }
    
    showStep(stepName);

    // If going to preferences with a substep specified, navigate to that substep
    // Only proceed if pickup is selected (which is when substeps exist)
    if (stepName === STEPS.PREFERENCES && substepName && isPickupSelected()) {
      loadSubsteps();
      if (substeps.length > 0) {
        const substepIndex = substeps.findIndex(substep => {
          const headers = substep.querySelectorAll('h3');
          // Match by content or data attribute if available
          return Array.from(headers).some(h => h.textContent.toLowerCase().includes(substepName.toLowerCase()));
        });

        if (substepIndex !== -1) {
          showSubstep(substepIndex);
        }
      }
    }

    updateFooterButtons();
  }

  /**
   * Attach button event listeners
   */
  function attachButtonListeners() {
    const backBtn = getBackButton();
    const continueBtn = getContinueButton();

    if (backBtn) {
      backBtn.addEventListener('click', function(e) {
        e.preventDefault();
        goToPreviousStep();
      });
    }

    if (continueBtn) {
      continueBtn.addEventListener('click', function(e) {
        // Otherwise advance to next step
        goToNextStep();
      });
    }

    // Attach listener to browse menu button
    const browseMenuBtn = document.getElementById('browse-menu-button');
    if (browseMenuBtn) {
      browseMenuBtn.addEventListener('click', function(e) {
        e.preventDefault();
        if (typeof window.browseMeals === 'function') {
          window.browseMeals();
        }
      });
    }

    // Attach event listeners to edit buttons
    document.querySelectorAll('[data-edit-step]').forEach(editBtn => {
      editBtn.addEventListener('click', function(e) {
        e.preventDefault();
        const targetStep = this.getAttribute('data-edit-step');
        const targetSubstep = this.getAttribute('data-edit-substep');
        goToStepAndSubstep(targetStep, targetSubstep);
      });
    });
  }

  /**
   * Check if user has saved selections in localStorage
   */
  function hasSavedSelections() {
    const deliveryType = window.EtStorage.local.getItem('selected_delivery_type');
    const fulfillmentDate = window.EtStorage.local.getItem('selected_fulfillment_date');
    const zipCode = window.EtStorage.local.getItem('selected_zip_code');
    const fulfillmentTime = window.EtStorage.local.getItem('selected_fulfillment_time');

    // For delivery, all four should be present
    // For pickup, delivery type, date, and zip code should be present (time is optional for pickup)
    if (deliveryType === 'delivery') {
      return !!(deliveryType && fulfillmentDate && zipCode && fulfillmentTime);
    } else if (deliveryType === 'pickup') {
      return !!(deliveryType && fulfillmentDate && zipCode);
    }

    return false;
  }

  /**
   * Reset state when modal is hidden/closed
   */
  function resetState() {
    isEditingFromSummary = false;
    currentSubstepIndex = 0;
    substeps = [];
  }

  /**
   * Re-initialize when modal opens
   */
  function reinitializeOnModalOpen() {
    resetState();
    // Check if user has saved selections and skip to summary if so
    const startStep = hasSavedSelections() ? STEPS.SUMMARY : STEPS.ZIP;
    showStep(startStep);
  }

  /**
   * Initialize the step navigation
   */
  function init() {
    // Expose functions to global scope for external use if needed (do this first)
    window.deliveryStepNavigation = {
      goToStep: showStep,
      goToNextStep,
      goToPreviousStep,
      getCurrentStep: () => currentStep,
      currentSelections: {},
      resetState: resetState
    };

    // Check if user has saved selections and skip to summary if so
    const startStep = hasSavedSelections() ? STEPS.SUMMARY : STEPS.ZIP;
    showStep(startStep);

    // Attach event listeners
    attachButtonListeners();

    // Monitor modal visibility and reinitialize when opened
    const deliveryModal = document.getElementById('delivery-modal');
    if (deliveryModal) {
      const visibilityObserver = new MutationObserver(() => {
        // When modal becomes visible (hidden class removed), reinitialize
        if (!deliveryModal.classList.contains('hidden')) {
          reinitializeOnModalOpen();
        }
      });

      visibilityObserver.observe(deliveryModal, { attributes: true, attributeFilter: ['class'] });

      // Monitor step visibility and re-enforce correct step visibility
      // This accounts for delivery-modal.js potentially unhiding steps
      const stepObserver = new MutationObserver(() => {
        if (deliveryModal && !deliveryModal.classList.contains('hidden')) {
          // Check all steps and ensure only current step is visible
          STEP_ORDER.forEach(step => {
            const element = getStepElement(step);
            if (element) {
              if (step === currentStep) {
                if (element.classList.contains('hidden')) {
                  element.classList.remove('hidden');
                }
              } else {
                if (!element.classList.contains('hidden')) {
                  element.classList.add('hidden');
                }
              }
            }
          });
        }
      });

      // Start observing the modal wrapper for any class changes
      stepObserver.observe(deliveryModal, { attributes: true, subtree: true, attributeFilter: ['class'] });
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
